import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Global cache for flags that failed to load to avoid repeated 404 requests
const missingFlags = new Set();

const FlagIcon = ({ langValue, flagBaseUrl }) => {
  const [error, setError] = useState(false);

  // Reset error state when language changes or if it's already in missingFlags
  useEffect(() => {
    setError(missingFlags.has(langValue));
  }, [langValue]);

  if (error) {
    return <span>🌐</span>;
  }

  return (
    <>
      <img 
        src={`${flagBaseUrl}/${langValue}/flag`} 
        alt="" 
        width="20" 
        className="rounded-sm" 
        onError={() => { 
          missingFlags.add(langValue);
          setError(true);
        }}
      />
    </>
  );
};

const LanguageSelector = ({ languages, darkMode }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState({ left: 0, width: 240 });
  const dropdownRef = useRef(null);

  // Base URL for flags from local backend proxy
  const flagBaseUrl = '/backend/api/public/countries';

  const currentLanguage = languages.find(lang => i18n.language && i18n.language.startsWith(lang.value)) || languages[0];

  const updateMenuPosition = () => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const menuWidth = Math.min(240, viewportWidth - 16);

    // Prefer opening to the right (aligned with button left) if it fits in viewport;
    // otherwise align with button right, and always clamp within [8px, viewportWidth - 8px].
    let leftOnScreen = (rect.left + menuWidth <= viewportWidth - 8)
      ? rect.left
      : (rect.right - menuWidth);

    leftOnScreen = Math.max(8, Math.min(leftOnScreen, viewportWidth - menuWidth - 8));

    setMenuPosition({
      left: leftOnScreen - rect.left,
      width: menuWidth
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      window.addEventListener('resize', updateMenuPosition);
      return () => window.removeEventListener('resize', updateMenuPosition);
    }
  }, [isOpen]);

  const filteredLanguages = languages.filter(lang => 
    lang.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
    lang.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (langValue) => {
    i18n.changeLanguage(langValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      <button 
        className={`btn btn-sm rounded-pill px-2 px-sm-3 fw-bold border shadow-sm d-flex align-items-center justify-content-center gap-1 gap-sm-2 ${darkMode ? 'btn-outline-light' : 'btn-outline-dark'}`}
        type="button"
        onClick={handleToggle}
        style={{ height: '34px' }}
        title={currentLanguage?.label || currentLanguage?.value?.toUpperCase()}
      >
        <FlagIcon key={currentLanguage?.value} langValue={currentLanguage?.value} flagBaseUrl={flagBaseUrl} />
        <span className="d-none d-sm-inline">{currentLanguage?.value?.toUpperCase() || '??'}</span>
      </button>

      {isOpen && (
        <div 
          className={`dropdown-menu show shadow-lg border-0 p-2 mt-2 ${darkMode ? 'bg-dark border-secondary' : 'bg-white'}`}
          style={{ 
            position: 'absolute', 
            left: `${menuPosition.left}px`,
            right: 'auto',
            width: `${menuPosition.width}px`,
            minWidth: 'unset',
            maxHeight: '350px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1050
          }}
        >
          <div className="px-2 pb-2 pt-1 border-bottom mb-2 sticky-top bg-inherit">
            <input 
              type="text" 
              className={`form-control form-control-sm rounded-pill border shadow-sm ${darkMode ? 'bg-secondary text-white border-secondary' : ''}`}
              placeholder={t('search_language_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredLanguages.length > 0 ? (
              filteredLanguages.map((lang) => (
                <button
                  key={lang.value}
                  className={`dropdown-item d-flex align-items-center gap-3 rounded-2 py-2 ${darkMode ? 'text-light' : ''} ${i18n.language && i18n.language.startsWith(lang.value) ? 'active bg-primary text-white' : ''}`}
                  onClick={() => handleSelect(lang.value)}
                  type="button"
                >
                  <span style={{ width: '25px', display: 'flex', justifyContent: 'center' }}>
                    <FlagIcon langValue={lang.value} flagBaseUrl={flagBaseUrl} />
                  </span>
                  <div className="d-flex flex-column flex-grow-1 text-start">
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{lang.label}</span>
                    {lang.englishName && lang.englishName !== lang.label && (
                      <small className="opacity-50" style={{ fontSize: '0.7rem' }}>{lang.englishName}</small>
                    )}
                  </div>
                  <small className="opacity-50 text-uppercase">{lang.value}</small>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-muted small">{t('language_not_found')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
